import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewChecked, ElementRef, HostListener, ViewChild, ViewContainerRef, ComponentRef, Type, Optional } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DropdownCoordinatorService } from '../dropdown-coordinator.service';
import { Utils } from '@/utils/utils';

@Component({
    selector: 'custom-select',
    templateUrl: './custom-select.component.html',
    styleUrl: './custom-select.component.scss',
    standalone: true,
    imports: [ReactiveFormsModule]
})
export class CustomSelectComponent implements OnInit, OnDestroy, AfterViewChecked {
    @Input() label = '';
    @Input() placeholder = 'BUSCAR PRODUCTO POR NOMBRE O REFERENCIA...';
    @Input() formControlItem!: AbstractControl;
    @Input() isMultiple = false;
    @Input() autofocus = false
    /** Pass -1 to skip this field when tabbing through the form, without disabling click/focus. */
    @Input() tabindex: number | null = null;

    @Input() bindValue = 'value';
    @Input() bindLabel = 'label';
    @Input() bindSubLabel = '';
    @Input() bindExtra = '';

    /** Component class to instantiate when the user wants to create a new item from an empty search result. */
    @Input() createComponent: Type<any> | null = null;
    /** Extra @Input values to set on the dynamically created component (besides the ones it already sets, e.g. `id`). */
    @Input() createComponentInputs: Record<string, any> = {};

    @Output() selectionChange = new EventEmitter<any>();
    /** Emitted with the value returned by the dynamic component's `acceptFunction` output once the new item is saved. */
    @Output() itemCreated = new EventEmitter<any>();

    @ViewChild('selectInput') selectInput!: ElementRef<HTMLInputElement>;
    @ViewChild('createFormHost', { read: ViewContainerRef }) createFormHost?: ViewContainerRef;
    @ViewChild('optionsList') optionsList?: ElementRef<HTMLDivElement>;

    isOpen = false;
    showCreateForm = false;
    searchControl = new FormControl('');
    filteredOptions: any[] = [];

    private _options: any[] = [];
    private destroy$ = new Subject<void>();
    private isSettingInternalValue = false;
    private pendingCreateFormRender = false;
    private createFormRef?: ComponentRef<any>;

    @Input()
    set options(val: any[]) {
        this._options = val || [];
        this.filterOptions(this.searchControl?.value || '');
        this.syncSearchInputWithControlValue();
    }

    get options(): any[] {
        return this._options;
    }

    constructor(private elementRef: ElementRef, @Optional() private coordinator: DropdownCoordinatorService) { }

    ngOnInit() {
        this.searchControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => {
                if (this.isSettingInternalValue) return;
                this.filterOptions(value || '');
            });

        this.formControlItem?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.syncSearchInputWithControlValue());

        this.formControlItem?.statusChanges
            ?.pipe(takeUntil(this.destroy$))
            .subscribe(() => this.syncDisabledState());

        // Si otro custom-select de la misma vista se abre, cerramos este
        this.coordinator?.opened$
            .pipe(takeUntil(this.destroy$))
            .subscribe(source => {
                if (source !== this && this.isOpen) {
                    this.closeDropdown();
                }
            });

        this.syncDisabledState();
        this.syncSearchInputWithControlValue();
    }

    private syncDisabledState() {
        if (this.isDisabled) {
            this.searchControl.disable({ emitEvent: false });
        } else {
            this.searchControl.enable({ emitEvent: false });
        }
    }

    ngAfterViewInit() {
        if (this.autofocus) {
            setTimeout(() => {
                this.selectInput?.nativeElement?.focus();
            }, 50);
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewChecked() {
        if (this.pendingCreateFormRender && this.createFormHost) {
            this.pendingCreateFormRender = false;
            this.renderCreateForm();
        }
    }

    get isDisabled(): boolean {
        return !!this.formControlItem?.disabled;
    }

    openCreateForm(event?: MouseEvent) {
        event?.stopPropagation();

        if (this.isDisabled || !this.createComponent) return;

        // stopPropagation() de arriba impide que el clickOut() de document de otros
        // custom-select abiertos se dispare, así que avisamos explícitamente para que se cierren
        this.coordinator?.notifyOpened(this);

        this.isOpen = false;
        this.showCreateForm = true;
        this.pendingCreateFormRender = true;
    }

    private renderCreateForm() {
        if (!this.createFormHost || !this.createComponent) return;

        this.createFormHost.clear();
        this.createFormRef = this.createFormHost.createComponent(this.createComponent);

        Object.assign(this.createFormRef.instance, this.createComponentInputs);

        const instance: any = this.createFormRef.instance;
        instance.closeFunction?.subscribe?.(() => this.closeCreateForm());
        instance.acceptFunction?.subscribe?.((result: any) => this.onCreateFormAccepted(result));
    }

    private closeCreateForm() {
        this.showCreateForm = false;
        this.createFormHost?.clear();
        this.createFormRef = undefined;
    }

    private onCreateFormAccepted(result: any) {
        this.itemCreated.emit(result);
        this.closeCreateForm();
    }

    openDropdown(event?: MouseEvent) {

        event?.stopPropagation();

        if (this.isDisabled) return;

        if (this.isOpen) {
            this.selectInput?.nativeElement.focus();
            return;
        }

        // stopPropagation() de arriba impide que el clickOut() de document de otros
        // custom-select abiertos se dispare, así que avisamos explícitamente para que se cierren
        this.coordinator?.notifyOpened(this);

        this.isOpen = true;
        this.isSettingInternalValue = true;
        this.searchControl.setValue('');
        this.isSettingInternalValue = false;
        this.filterOptions('');

        setTimeout(() => {
            if (this.selectInput?.nativeElement) {
                this.selectInput.nativeElement.focus();
            }
        }, 20);
    }

    closeDropdown() {
        this.isOpen = false;
        this.syncSearchInputWithControlValue();
    }

    /** Tab en el último item del dropdown vuelve al primero, y Shift+Tab en el primero va al último. */
    onOptionTab(event: Event, isFirst: boolean, isLast: boolean): void {
        const keyboardEvent = event as KeyboardEvent;
        if (!keyboardEvent.shiftKey && isLast) {
            const firstButton = this.optionsList?.nativeElement.querySelector<HTMLButtonElement>('button');
            if (firstButton) {
                event.preventDefault();
                firstButton.focus();
            }
        } else if (keyboardEvent.shiftKey && isFirst) {
            const buttons = this.optionsList?.nativeElement.querySelectorAll<HTMLButtonElement>('button');
            const lastButton = buttons?.[buttons.length - 1];
            if (lastButton) {
                event.preventDefault();
                lastButton.focus();
            }
        }
    }

    onEnterPressed(event: Event): void {
        event.preventDefault();

        if (this.isOpen && this.filteredOptions && this.filteredOptions.length > 0) {
            this.selectOption(this.filteredOptions[0]);

            if (!this.isMultiple) {
                this.isOpen = false;
            }
        }
    }

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.closeDropdown();
        }
    }

    private filterOptions(searchTerm: string) {
        const term = searchTerm.toLowerCase().trim();
        if (!term) {
            this.filteredOptions = this._options;
            return;
        }
        this.filteredOptions = this._options.filter(opt => {
            const labelText = opt[this.bindLabel] ? String(opt[this.bindLabel]) : '';
            const subLabelText = this.bindSubLabel && opt[this.bindSubLabel] ? String(opt[this.bindSubLabel]) : '';
            return labelText.toLowerCase().includes(term) || subLabelText.toLowerCase().includes(term);
        });
    }

    get isRequired(): boolean {
        return !!this.formControlItem?.hasValidator?.(Validators.required);
    }

    isSelected(option: any): boolean {
        const currentControlValue = this.formControlItem?.value;
        const optionValue = option[this.bindValue];

        if (this.isMultiple) {
            return Array.isArray(currentControlValue) && currentControlValue.includes(optionValue);
        }
        return currentControlValue === optionValue;
    }

    selectOption(option: any) {
        if (this.isMultiple) {
            this.toggleMultiple(option);
            this.selectInput?.nativeElement.focus();
        } else {
            this.selectSingle(option);
            this.closeDropdown();
            // Un clic en una opción no dispara ningún keydown, así que el "Enter avanza como
            // Tab" global (app.ts) no se entera por sí solo — lo replicamos aquí explícitamente.
            if (this.selectInput?.nativeElement) {
                Utils.focusNextFormField(this.selectInput.nativeElement);
            }
        }
    }

    private selectSingle(option: any) {
        const optionValue = option[this.bindValue];
        this.formControlItem.setValue(optionValue);
        this.formControlItem.markAsDirty();

        this.selectionChange.emit(optionValue);
    }

    private toggleMultiple(option: any) {
        const optionValue = option[this.bindValue];
        let currentValues: any[] = Array.isArray(this.formControlItem.value) ? [...this.formControlItem.value] : [];

        if (currentValues.includes(optionValue)) {
            currentValues = currentValues.filter(val => val !== optionValue);
        } else {
            currentValues.push(optionValue);
        }

        this.formControlItem.setValue(currentValues);
        this.formControlItem.markAsDirty();

        this.selectionChange.emit(currentValues);
    }

    private syncSearchInputWithControlValue() {
        if (this.isMultiple) {
            this.isSettingInternalValue = true;
            const totalSelected = Array.isArray(this.formControlItem?.value) ? this.formControlItem.value.length : 0;
            this.searchControl.setValue(totalSelected > 0 ? `SELECCIONADOS (${totalSelected})` : '', { emitEvent: false });
            this.isSettingInternalValue = false;
        } else {
            const value = this.formControlItem?.value;
            const selectedOption = this._options.find(opt => opt[this.bindValue] === value);
            this.isSettingInternalValue = true;

            if (selectedOption) {
                const mainLabel = selectedOption[this.bindLabel];
                const subLabel = this.bindSubLabel && selectedOption[this.bindSubLabel] ? selectedOption[this.bindSubLabel] : null;
                const displayText = subLabel ? `${mainLabel} (REF: ${subLabel})` : mainLabel;
                this.searchControl.setValue(displayText, { emitEvent: false });
            } else {
                this.searchControl.setValue('', { emitEvent: false });
            }
            this.isSettingInternalValue = false;
        }
    }
}